// Part of the Chili3d Project, under the AGPL-3.0 License.
// See LICENSE file in the project root for full license information.

import { IApplication } from "../application";
import { Result } from "../foundation";
import { IService } from "../service";

export interface CityGMLConversionOptions {
    defaultHeight?: number;
    limit?: number;
    debug?: boolean;
    method?: "auto" | "solid" | "sew" | "extrude";
    sewTolerance?: number;
    reprojectTo?: string;
    sourceCrs?: string;
    autoReproject?: boolean;
    buildingIds?: string[];
    filterAttribute?: string;
}

export interface ICityGMLService extends IService {
    convertToStep(cityGmlFile: File, options?: CityGMLConversionOptions): Promise<Result<Blob>>;
    validateCityGML(cityGmlFile: File): Promise<Result<ValidationResponse>>;
    searchByAddress(query: string, options?: PlateauSearchOptions): Promise<Result<PlateauSearchResponse>>;
    fetchAndConvertByAddress(query: string, options?: PlateauFetchAndConvertOptions): Promise<Result<Blob>>;
    searchByBuildingId(
        buildingId: string,
        options?: PlateauBuildingIdSearchOptions,
    ): Promise<Result<PlateauBuildingIdSearchResponse>>;
    searchByBuildingIdAndMesh(
        buildingId: string,
        meshCode: string,
        options?: PlateauBuildingIdWithMeshSearchOptions,
    ): Promise<Result<PlateauBuildingIdSearchResponse>>;
    unfoldTexturedByBuildingIdAndMesh(
        buildingId: string,
        meshCode: string,
        options?: PlateauTexturedUnfoldOptions,
    ): Promise<Result<PlateauTexturedUnfoldResponse>>;
    batchSearchByBuildingIds(
        requests: BatchBuildingRequest[],
    ): Promise<Result<PlateauBatchBuildingResponse>>;
}

export interface ValidationResponse {
    valid: boolean;
    buildings_with_footprints: number;
    sample_building_id?: string;
    notes: string;
}

export interface BuildingInfo {
    building_id?: string;
    gml_id: string;
    latitude: number;
    longitude: number;
    distance_meters: number;
    height?: number;
    usage?: string;
    measured_height?: number;
    building_structure_type?: string;
    has_lod2?: boolean;
    has_lod3?: boolean;
    name?: string;
    relevance_score?: number;
    name_similarity?: number;
    match_reason?: string;
}

export interface GeocodingResult {
    query: string;
    latitude: number;
    longitude: number;
    display_name: string;
    osm_type?: string;
    osm_id?: number;
}

export interface PlateauSearchResponse {
    success: boolean;
    geocoding?: GeocodingResult;
    buildings: BuildingInfo[];
    found_count: number;
    search_mode?: string;
    error?: string;
}

export interface PlateauSearchOptions {
    radius?: number; // Search radius in degrees (default: 0.001 ≈ 100m)
    limit?: number; // Maximum number of buildings to return
    nameFilter?: string; // Building name to filter/rank by
    searchMode?: "distance" | "name" | "hybrid"; // Search ranking strategy
}

export interface PlateauFetchAndConvertOptions extends PlateauSearchOptions {
    buildingLimit?: number; // Number of buildings to convert
    buildingIds?: string[]; // Specific building IDs to convert (gml:id format)
    debug?: boolean;
    method?: "auto" | "solid" | "sew" | "extrude";
    autoReproject?: boolean;
    mergeBuildingParts?: boolean; // Merge BuildingParts into single solid (default: false, preserves detail)
}

export interface PlateauBuildingIdSearchOptions {
    debug?: boolean;
}

export interface PlateauBuildingIdWithMeshSearchOptions {
    debug?: boolean;
    mergeBuildingParts?: boolean;
    lod?: string | null; // "LOD1" | "LOD2" | "LOD3" | null (auto fallback)
}

export interface PlateauTexturedUnfoldOptions extends PlateauBuildingIdWithMeshSearchOptions {
    precisionMode?: "standard" | "high" | "maximum" | "ultra";
    shapeFixLevel?: "minimal" | "standard" | "aggressive" | "ultra";
    method?: "auto" | "solid" | "sew" | "extrude";
    autoReproject?: boolean;
    layoutMode?: "canvas" | "paged";
    pageFormat?: "A4" | "A3" | "Letter";
    pageOrientation?: "portrait" | "landscape";
    scaleFactor?: number;
    mirrorHorizontal?: boolean;
    maxFaces?: number;
    returnFaceNumbers?: boolean;
}

export interface PlateauBuildingIdSearchResponse {
    success: boolean;
    building?: BuildingInfo;
    municipality_code?: string;
    municipality_name?: string;
    citygml_file?: string;
    total_buildings_in_file?: number;
    error?: string;
    error_details?: string;
}

export interface BatchBuildingRequest {
    buildingId: string;
    meshCode: string;
}

export interface PlateauBatchBuildingResponse {
    results: PlateauBuildingIdSearchResponse[];
    total_requested: number;
    total_success: number;
    total_failed: number;
}

export interface PlateauTexturedUnfoldResponse {
    svg_content: string;
    stats?: any;
    face_numbers?: Array<{ faceIndex: number; faceNumber: number }>;
    texture_mappings?: Array<{
        faceNumber: number;
        patternId: string;
        tileCount: number;
        rotation?: number;
    }>;
    warnings?: Array<{
        type: string;
        message: string;
        details?: any;
    }>;
    texture_stats?: any;
}

export class CityGMLService implements ICityGMLService {
    private readonly baseUrl: string;

    constructor(baseUrl: string = "http://localhost:8001/api") {
        this.baseUrl = baseUrl;
    }

    register(_app: IApplication): void {
        // Service registration
    }

    start(): void {
        // Service start
    }

    stop(): void {
        // Service stop
    }

    async convertToStep(cityGmlFile: File, options?: CityGMLConversionOptions): Promise<Result<Blob>> {
        try {
            // Validate file extension
            if (!this.isValidCityGMLFile(cityGmlFile)) {
                return Result.err("Invalid CityGML file. Please select a .gml or .xml file.");
            }

            const formData = new FormData();
            formData.append("file", cityGmlFile);

            // Add optional parameters
            if (options?.defaultHeight !== undefined) {
                formData.append("default_height", options.defaultHeight.toString());
            }
            if (options?.limit !== undefined) {
                formData.append("limit", options.limit.toString());
            }
            if (options?.debug !== undefined) {
                formData.append("debug", options.debug.toString());
            }
            if (options?.method) {
                formData.append("method", options.method);
            }
            if (options?.sewTolerance !== undefined) {
                formData.append("sew_tolerance", options.sewTolerance.toString());
            }
            if (options?.reprojectTo) {
                formData.append("reproject_to", options.reprojectTo);
            }
            if (options?.sourceCrs) {
                formData.append("source_crs", options.sourceCrs);
            }
            if (options?.autoReproject !== undefined) {
                formData.append("auto_reproject", options.autoReproject.toString());
            }
            if (options?.buildingIds && options.buildingIds.length > 0) {
                formData.append("building_ids", options.buildingIds.join(","));
            }
            if (options?.filterAttribute) {
                formData.append("filter_attribute", options.filterAttribute);
            }

            const response = await fetch(`${this.baseUrl}/citygml/to-step`, {
                method: "POST",
                body: formData,
            });

            if (!response.ok) {
                let errorMessage: string;
                if (response.status === 400) {
                    const errorData = await response.json().catch(() => null);
                    errorMessage =
                        errorData?.detail || "CityGML file conversion failed. Please check the file format.";
                } else if (response.status === 413) {
                    errorMessage =
                        "File size too large. Please use a smaller file or limit the number of buildings.";
                } else if (response.status === 503) {
                    errorMessage =
                        "Backend service unavailable. Please ensure the conversion service is running.";
                } else {
                    errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                }
                return Result.err(errorMessage);
            }

            // Get the response as blob
            const blob = await response.blob();
            return Result.ok(blob);
        } catch (error) {
            if (error instanceof Error) {
                // Check for network errors
                if (error.message.includes("fetch")) {
                    return Result.err(
                        "Cannot connect to CityGML conversion service. Please ensure the backend is running on " +
                            this.baseUrl,
                    );
                }
                return Result.err(error.message);
            }
            return Result.err("Unknown error during CityGML conversion");
        }
    }

    async validateCityGML(cityGmlFile: File): Promise<Result<ValidationResponse>> {
        try {
            if (!this.isValidCityGMLFile(cityGmlFile)) {
                return Result.err("Invalid CityGML file. Please select a .gml or .xml file.");
            }

            const formData = new FormData();
            formData.append("file", cityGmlFile);
            formData.append("limit", "10");

            const response = await fetch(`${this.baseUrl}/citygml/validate`, {
                method: "POST",
                body: formData,
            });

            if (!response.ok) {
                return Result.err(`Validation failed: ${response.status} ${response.statusText}`);
            }

            const validationData = await response.json();
            return Result.ok(validationData);
        } catch (error) {
            return Result.err(error instanceof Error ? error.message : "Unknown validation error");
        }
    }

    async searchByAddress(
        query: string,
        options?: PlateauSearchOptions,
    ): Promise<Result<PlateauSearchResponse>> {
        try {
            const requestBody = {
                query,
                radius: options?.radius ?? 0.001,
                limit: options?.limit ?? 10,
                auto_select_nearest: true,
                name_filter: options?.nameFilter,
                search_mode: options?.searchMode ?? "hybrid",
            };

            const response = await fetch(`${this.baseUrl}/plateau/search-by-address`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                let errorMessage: string;
                if (response.status === 404) {
                    errorMessage = "Address or buildings not found";
                } else if (response.status === 500) {
                    const errorData = await response.json().catch(() => null);
                    errorMessage = errorData?.detail || "Search failed due to server error";
                } else {
                    errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                }
                return Result.err(errorMessage);
            }

            const data: PlateauSearchResponse = await response.json();
            return Result.ok(data);
        } catch (error) {
            if (error instanceof Error) {
                if (error.message.includes("fetch")) {
                    return Result.err(
                        "Cannot connect to PLATEAU search service. Please ensure the backend is running on " +
                            this.baseUrl,
                    );
                }
                return Result.err(error.message);
            }
            return Result.err("Unknown error during PLATEAU address search");
        }
    }

    async fetchAndConvertByAddress(
        query: string,
        options?: PlateauFetchAndConvertOptions,
    ): Promise<Result<Blob>> {
        try {
            const formData = new FormData();
            formData.append("query", query);

            if (options?.radius !== undefined) {
                formData.append("radius", options.radius.toString());
            }
            if (options?.buildingLimit !== undefined) {
                formData.append("building_limit", options.buildingLimit.toString());
            }
            if (options?.buildingIds && options.buildingIds.length > 0) {
                formData.append("building_ids", options.buildingIds.join(","));
            }
            if (options?.debug !== undefined) {
                formData.append("debug", options.debug.toString());
            }
            if (options?.method) {
                formData.append("method", options.method);
            }
            if (options?.autoReproject !== undefined) {
                formData.append("auto_reproject", options.autoReproject.toString());
            }
            if (options?.mergeBuildingParts !== undefined) {
                formData.append("merge_building_parts", options.mergeBuildingParts.toString());
            }

            const response = await fetch(`${this.baseUrl}/plateau/fetch-and-convert`, {
                method: "POST",
                body: formData,
            });

            if (!response.ok) {
                let errorMessage: string;
                if (response.status === 404) {
                    errorMessage = "No buildings found at the specified location";
                } else if (response.status === 400) {
                    const errorData = await response.json().catch(() => null);
                    errorMessage = errorData?.detail || "Invalid request parameters";
                } else if (response.status === 500) {
                    const errorData = await response.json().catch(() => null);
                    errorMessage = errorData?.detail || "Conversion failed due to server error";
                } else {
                    errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                }
                return Result.err(errorMessage);
            }

            const blob = await response.blob();
            return Result.ok(blob);
        } catch (error) {
            if (error instanceof Error) {
                if (error.message.includes("fetch")) {
                    return Result.err(
                        "Cannot connect to PLATEAU conversion service. Please ensure the backend is running on " +
                            this.baseUrl,
                    );
                }
                return Result.err(error.message);
            }
            return Result.err("Unknown error during PLATEAU fetch and convert");
        }
    }

    async searchByBuildingId(
        buildingId: string,
        options?: PlateauBuildingIdSearchOptions,
    ): Promise<Result<PlateauBuildingIdSearchResponse>> {
        try {
            const requestBody = {
                building_id: buildingId,
                debug: options?.debug ?? false,
            };

            const response = await fetch(`${this.baseUrl}/plateau/search-by-id`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                let errorMessage: string;
                if (response.status === 404) {
                    const errorData = await response.json().catch(() => null);
                    errorMessage = errorData?.error_details || "Building not found";
                } else if (response.status === 400) {
                    const errorData = await response.json().catch(() => null);
                    errorMessage = errorData?.error_details || "Invalid building ID format";
                } else if (response.status === 500) {
                    const errorData = await response.json().catch(() => null);
                    errorMessage = errorData?.error_details || "Search failed due to server error";
                } else {
                    errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                }
                return Result.err(errorMessage);
            }

            const data: PlateauBuildingIdSearchResponse = await response.json();
            return Result.ok(data);
        } catch (error) {
            if (error instanceof Error) {
                if (error.message.includes("fetch")) {
                    return Result.err(
                        "Cannot connect to PLATEAU building ID search service. Please ensure the backend is running on " +
                            this.baseUrl,
                    );
                }
                return Result.err(error.message);
            }
            return Result.err("Unknown error during PLATEAU building ID search");
        }
    }

    async searchByBuildingIdAndMesh(
        buildingId: string,
        meshCode: string,
        options?: PlateauBuildingIdWithMeshSearchOptions,
    ): Promise<Result<PlateauBuildingIdSearchResponse>> {
        try {
            const requestBody = {
                building_id: buildingId,
                mesh_code: meshCode,
                debug: options?.debug ?? false,
                merge_building_parts: options?.mergeBuildingParts ?? false,
            };

            const response = await fetch(`${this.baseUrl}/plateau/search-by-id-and-mesh`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                let errorMessage: string;
                if (response.status === 404) {
                    const errorData = await response.json().catch(() => null);
                    errorMessage = errorData?.error_details || "Building not found in mesh area";
                } else if (response.status === 400) {
                    const errorData = await response.json().catch(() => null);
                    errorMessage = errorData?.error_details || "Invalid building ID or mesh code format";
                } else if (response.status === 500) {
                    const errorData = await response.json().catch(() => null);
                    errorMessage = errorData?.error_details || "Search failed due to server error";
                } else {
                    errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                }
                return Result.err(errorMessage);
            }

            const data: PlateauBuildingIdSearchResponse = await response.json();
            return Result.ok(data);
        } catch (error) {
            if (error instanceof Error) {
                if (error.message.includes("fetch")) {
                    return Result.err(
                        "Cannot connect to PLATEAU building ID + mesh code search service. Please ensure the backend is running on " +
                            this.baseUrl,
                    );
                }
                return Result.err(error.message);
            }
            return Result.err("Unknown error during PLATEAU building ID + mesh code search");
        }
    }

    async fetchAndConvertByBuildingIdAndMesh(
        buildingId: string,
        meshCode: string,
        options?: PlateauBuildingIdWithMeshSearchOptions,
    ): Promise<Result<Blob>> {
        try {
            const requestBody: Record<string, unknown> = {
                building_id: buildingId,
                mesh_code: meshCode,
                merge_building_parts: options?.mergeBuildingParts ?? false,
                debug: options?.debug ?? false,
                precision_mode: "ultra",
                shape_fix_level: "minimal",
                method: "solid",
                auto_reproject: true,
            };
            if (options?.lod) {
                requestBody["lod"] = options.lod;
            }

            const response = await fetch(`${this.baseUrl}/plateau/fetch-by-id-and-mesh`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                let errorMessage: string;
                if (response.status === 404) {
                    errorMessage = "Building not found in mesh area";
                } else if (response.status === 400 || response.status === 422) {
                    const errorData = await response.json().catch(() => null);
                    errorMessage = errorData?.detail || "Invalid request parameters";
                } else if (response.status === 500) {
                    const errorData = await response.json().catch(() => null);
                    errorMessage = errorData?.detail || "Conversion failed due to server error";
                } else {
                    errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                }
                return Result.err(errorMessage);
            }

            const blob = await response.blob();
            return Result.ok(blob);
        } catch (error) {
            if (error instanceof Error) {
                if (error.message.includes("fetch")) {
                    return Result.err(
                        "Cannot connect to PLATEAU fetch and convert service. Please ensure the backend is running on " +
                            this.baseUrl,
                    );
                }
                return Result.err(error.message);
            }
            return Result.err("Unknown error during PLATEAU fetch and convert");
        }
    }

    async unfoldTexturedByBuildingIdAndMesh(
        buildingId: string,
        meshCode: string,
        options?: PlateauTexturedUnfoldOptions,
    ): Promise<Result<PlateauTexturedUnfoldResponse>> {
        try {
            const requestBody: Record<string, unknown> = {
                building_id: buildingId,
                mesh_code: meshCode,
                merge_building_parts: options?.mergeBuildingParts ?? false,
                debug: options?.debug ?? false,
                precision_mode: options?.precisionMode ?? "ultra",
                shape_fix_level: options?.shapeFixLevel ?? "minimal",
                method: options?.method ?? "solid",
                auto_reproject: options?.autoReproject ?? true,
                layout_mode: options?.layoutMode ?? "paged",
                page_format: options?.pageFormat ?? "A4",
                page_orientation: options?.pageOrientation ?? "portrait",
                scale_factor: options?.scaleFactor ?? 10,
                mirror_horizontal: options?.mirrorHorizontal ?? false,
                max_faces: options?.maxFaces ?? 20,
                return_face_numbers: options?.returnFaceNumbers ?? true,
            };
            if (options?.lod) {
                requestBody["lod"] = options.lod;
            }

            const response = await fetch(`${this.baseUrl}/plateau/unfold-textured-by-id-and-mesh`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                let errorMessage: string;
                if (response.status === 404) {
                    const errorData = await response.json().catch(() => null);
                    errorMessage = errorData?.detail || "Building not found in mesh area";
                } else if (response.status === 400 || response.status === 422) {
                    const errorData = await response.json().catch(() => null);
                    errorMessage = errorData?.detail || "Invalid request parameters";
                } else if (response.status === 500) {
                    const errorData = await response.json().catch(() => null);
                    errorMessage = errorData?.detail || "Textured unfold generation failed";
                } else {
                    errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                }
                return Result.err(errorMessage);
            }

            const data: PlateauTexturedUnfoldResponse = await response.json();
            return Result.ok(data);
        } catch (error) {
            if (error instanceof Error) {
                if (error.message.includes("fetch")) {
                    return Result.err(
                        "Cannot connect to PLATEAU textured unfold service. Please ensure the backend is running on " +
                            this.baseUrl,
                    );
                }
                return Result.err(error.message);
            }
            return Result.err("Unknown error during PLATEAU textured unfold");
        }
    }

    async batchSearchByBuildingIds(
        requests: BatchBuildingRequest[],
    ): Promise<Result<PlateauBatchBuildingResponse>> {
        try {
            if (requests.length === 0) {
                return Result.err("Batch request cannot be empty");
            }

            if (requests.length > 100) {
                return Result.err("Batch request cannot exceed 100 buildings");
            }

            // Convert to backend format (snake_case)
            const requestBody = {
                buildings: requests.map((req) => ({
                    building_id: req.buildingId,
                    mesh_code: req.meshCode,
                })),
            };

            const response = await fetch(`${this.baseUrl}/plateau/buildings/batch`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                let errorMessage: string;
                if (response.status === 400) {
                    const errorData = await response.json().catch(() => null);
                    errorMessage = errorData?.detail || "Invalid batch request parameters";
                } else if (response.status === 500) {
                    const errorData = await response.json().catch(() => null);
                    errorMessage = errorData?.detail || "Batch search failed due to server error";
                } else {
                    errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                }
                return Result.err(errorMessage);
            }

            const data: PlateauBatchBuildingResponse = await response.json();
            return Result.ok(data);
        } catch (error) {
            if (error instanceof Error) {
                if (error.message.includes("fetch")) {
                    return Result.err(
                        "Cannot connect to PLATEAU batch search service. Please ensure the backend is running on " +
                            this.baseUrl,
                    );
                }
                return Result.err(error.message);
            }
            return Result.err("Unknown error during PLATEAU batch search");
        }
    }

    private isValidCityGMLFile(file: File): boolean {
        const validExtensions = [".gml", ".xml"];
        const fileName = file.name.toLowerCase();
        return validExtensions.some((ext) => fileName.endsWith(ext));
    }
}
